import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, TrendingUp, ArrowRight, Check, Zap } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const PLAN_NAME = { starter: "Starter", growth: "Growth", enterprise: "Enterprise" };

function pctColor(p) {
  return p >= 90 ? "bg-red-400" : p >= 80 ? "bg-amber-400" : "fv-gold-gradient";
}

/** Intelligent Upgrade Modal — dynamic, ROI-oriented recommendation from real usage. */
export function UpgradeModal({ open, onOpenChange, intel, onUpgrade }) {
  const [plans, setPlans] = useState(null);
  const [cycle, setCycle] = useState("annual");
  useEffect(() => {
    if (open) api.get("/subscription/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, [open]);

  if (!intel) return null;
  const rec = intel.recommendation;
  const s = intel.scores;
  const targetId = rec?.target_plan || (s.plan_id === "starter" ? "growth" : "enterprise");
  const target = plans?.plans?.find((p) => p.id === targetId);
  const hot = (s.usage_items || []).filter((i) => !i.unlimited && i.limit)
    .sort((a, b) => (b.used / b.limit) - (a.used / a.limit)).slice(0, 3);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121214] border-[#D4AF37]/25 text-white max-w-lg" data-testid="upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D4AF37]" /> Recommended: {PLAN_NAME[targetId]}
          </DialogTitle>
          <DialogDescription className="text-[#71717A]">
            A plan recommendation based on your current usage and activity.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#A1A1AA]">{rec?.message || `Based on your usage, ${PLAN_NAME[targetId]} is the right fit.`}</p>

        {hot.length > 0 && (
          <div className="my-3 space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-[#71717A]">Your current usage</div>
            {hot.map((i) => {
              const p = Math.min(100, Math.round((i.used / i.limit) * 100));
              return (
                <div key={i.metric}>
                  <div className="flex justify-between text-xs mb-1"><span className="text-[#D4D4D8]">{i.label}</span><span className="text-[#A1A1AA]">{p}%</span></div>
                  <div className="h-1.5 rounded-full bg-white/8 overflow-hidden"><div className={`h-full ${pctColor(p)}`} style={{ width: `${p}%` }} /></div>
                </div>
              );
            })}
          </div>
        )}

        {target && (
          <>
            <div className="flex items-center gap-2 my-2">
              {["monthly", "annual"].map((c) => (
                <button key={c} onClick={() => setCycle(c)} data-testid={`upgrade-modal-cycle-${c}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${cycle === c ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#A1A1AA]"}`}>
                  {c === "monthly" ? "Monthly" : "Annual (2 months free)"}
                </button>
              ))}
            </div>
            <div className="rounded-xl border border-white/10 bg-[#0d0d0f] p-4">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-2xl font-heading fv-gold-text">${cycle === "annual" ? target.price_annual : target.price}</span>
                <span className="text-xs text-[#71717A]">/{cycle === "annual" ? "yr" : "mo"}</span>
              </div>
              {cycle === "annual" && (target.price * 12 - target.price_annual) > 0 && (
                <div className="text-[11px] font-semibold text-[#D4AF37] mb-3">Save ${(target.price * 12 - target.price_annual).toFixed(2)}/yr</div>
              )}
              <ul className="space-y-1.5">
                {(target.features || []).slice(0, 5).map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-[#D4D4D8]"><Check className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
            </div>
          </>
        )}

        <Button data-testid="upgrade-modal-confirm"
          onClick={() => { api.post("/subscription/track", { event: "upgrade_click", detail: { source: "modal", plan_id: targetId, billing_cycle: cycle } }).catch(() => {}); onUpgrade(targetId, cycle); }}
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full h-11 mt-2">
          Upgrade to {PLAN_NAME[targetId]} <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
        <p className="text-center text-[11px] text-[#52525B]">Prorated automatically · cancel anytime · data always preserved</p>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Non-blocking smart upgrade banner. Renders only when the Revenue Intelligence engine
 * returns an upgrade/annual recommendation (usage ~80%+ or monthly→annual saving).
 */
export function UpgradeNudge({ onUpgrade, compact }) {
  const navigate = useNavigate();
  const [intel, setIntel] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [modal, setModal] = useState(false);

  useEffect(() => {
    api.get("/subscription/intelligence").then(({ data }) => setIntel(data)).catch(() => {});
  }, []);

  const rec = intel?.recommendation;
  const show = rec && (rec.type === "upgrade" || rec.type === "annual") && !dismissed;
  useEffect(() => {
    if (show) api.post("/subscription/track", { event: "upgrade_impression", detail: { type: rec.type, source: compact ? "dashboard" : "billing" } }).catch(() => {});
  }, [show, rec, compact]);

  if (!show) return null;
  const s = intel.scores;
  const topUsage = (s.usage_items || []).filter((i) => !i.unlimited && i.limit)
    .map((i) => ({ ...i, pct: Math.round((i.used / i.limit) * 100) })).sort((a, b) => b.pct - a.pct)[0];

  const doUpgrade = (planId, cycle) => {
    if (onUpgrade) onUpgrade(planId, cycle);
    else navigate(`/billing?upgrade=${planId}`);
  };

  return (
    <>
      <div data-testid="upgrade-nudge"
        className="relative mb-6 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-r from-[#151310] to-[#121214] p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0">
          {rec.type === "annual" ? <TrendingUp className="w-5 h-5 text-[#0A0A0B]" /> : <Zap className="w-5 h-5 text-[#0A0A0B]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-medium text-sm" data-testid="upgrade-nudge-title">{rec.title}</div>
          <div className="text-[#A1A1AA] text-xs mt-0.5">{rec.message}</div>
          {topUsage && rec.type === "upgrade" && (
            <div className="mt-2 max-w-xs">
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden"><div className={`h-full ${pctColor(topUsage.pct)}`} style={{ width: `${Math.min(100, topUsage.pct)}%` }} /></div>
              <div className="text-[10px] text-[#71717A] mt-1">{topUsage.label}: {topUsage.used} / {topUsage.limit}</div>
            </div>
          )}
          <button onClick={() => setModal(true)} data-testid="upgrade-nudge-cta"
            className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#D4AF37] hover:gap-2.5 transition-all">
            {rec.cta} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <button onClick={() => { setDismissed(true); api.post("/subscription/track", { event: "upgrade_dismissed", detail: { type: rec.type, source: compact ? "dashboard" : "billing" } }).catch(() => {}); }} data-testid="upgrade-nudge-dismiss" className="text-[#52525B] hover:text-white transition-colors shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
      <UpgradeModal open={modal} onOpenChange={setModal} intel={intel} onUpgrade={doUpgrade} />
    </>
  );
}
